import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import './UnitDetails.css';
import UnitInfo from './UnitInfo/UnitInfo';
import PriceData from './PriceBar/PriceData';
import PriceGraph from './PriceGraph/PriceGraph';
import OffMarketDisplay from './OffMarketDisplay/OffMarketDisplay';

interface PropertyNameResponse {
  name: string;
}

interface AvailableStatus {
  date: string;
  available: string;
}

const UnitDetails: React.FC = () => {
  const { propertyId, unitNumber } = useParams<{ propertyId: string; unitNumber: string }>();
  const [propertyName, setPropertyName] = useState<string>('');
  const [isOnMarket, setIsOnMarket] = useState(true);
  const [dateSigned, setDateSigned] = useState<string>('');
  const [priceSigned, setPriceSigned] = useState<number>(0);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchPropertyName = async () => {
      // First check localStorage
      const storedName = localStorage.getItem('selectedPropertyName');
      if (storedName) {
        setPropertyName(storedName);
        return;
      }

      // If not in localStorage, fetch from API
      try {
        const effectivePropertyId = propertyId || localStorage.getItem('selectedPropertyId');
        if (!effectivePropertyId) {
          throw new Error('Missing property ID');
        }

        const response = await fetch(`https://${process.env.REACT_APP_BASE_URL}/getName/${effectivePropertyId}`);
        const data: PropertyNameResponse[] = await response.json();
        
        if (data.length > 0) {
          const name = data[0].name;
          setPropertyName(name);
          // Store in localStorage for future use
          localStorage.setItem('selectedPropertyName', name);
        }
      } catch (error) {
        console.error('Error fetching property name:', error);
      }
    };

    const fetchUnitDetails = async () => {
      try {
        const effectivePropertyId = propertyId || localStorage.getItem('selectedPropertyId');
        const effectiveUnitNumber = unitNumber || localStorage.getItem('selectedUnitNumber');

        if (!effectivePropertyId || !effectiveUnitNumber) {
          throw new Error('Missing property or unit information');
        }

        // Fetch available status
        const statusResponse = await fetch(
          `https://${process.env.REACT_APP_BASE_URL}/getAvailableStatus/${effectivePropertyId}/${effectiveUnitNumber}`
        );
        const statusData: AvailableStatus[] = await statusResponse.json();
        
        if (statusData && statusData.length > 0) {
          setIsOnMarket(statusData[0].available === 'true');
        }

        // Fetch unit details for date and price
        const detailsResponse = await fetch(
          `https://${process.env.REACT_APP_BASE_URL}/getUnitDetails/${effectivePropertyId}/${effectiveUnitNumber}`
        );
        const detailsData = await detailsResponse.json();
        
        if (detailsData && detailsData[0]) {
          setDateSigned(detailsData[0].date_signed || 'N/A');
          setPriceSigned(detailsData[0].price_signed || 0);
        }
      } catch (error) {
        console.error('Error fetching unit details:', error);
      }
    };

    fetchPropertyName();
    fetchUnitDetails();
  }, [propertyId, unitNumber]);

  const handleStartOver = () => {
    // Clear the stored property and unit information
    localStorage.removeItem('selectedPropertyId');
    localStorage.removeItem('selectedUnitNumber');
    localStorage.removeItem('selectedPropertyName');
    // Navigate back to the root path
    navigate('/search');
  };

  return (
    <div className="unit-details-container">
      <div className="unit-details-content">
        <div className="unit-details-header">
          <h1 className="property-name">
            {propertyName || 'Loading...'}
            <span className="unit-number">- {unitNumber}</span>
          </h1>
          <button className="start-over-button" onClick={handleStartOver}>
            Start Over
          </button>
        </div>
        <UnitInfo />
        {isOnMarket ? (
          <>
            <PriceData />
            <PriceGraph />
          </>
        ) : (
          <OffMarketDisplay />
        )}
      </div>
    </div>
  );
};

export default UnitDetails; 